import React, { useCallback, useEffect, useState } from 'react';

import Button from 'components/shared/Button/Button';
import Typography from 'components/shared/Typography/Typography';

import { getGroupedColumnField } from '../lib/helpers';
import { HEADER_NO_SORT_RESERVE, HEADER_SORT_RESERVE } from '../lib/headerMinWidth';

const getButtonClasses = (isActive) => {
  return isActive ? 'opacity-0 group-hover:opacity-100' : 'hidden';
};

export const TableHeaderComponent = (props) => {
  const { displayName, column, api, setSort, enableSorting } = props;

  const [columnWidth, setColumnWidth] = useState(() => column.getActualWidth() || 0);
  const [currentSort, setCurrentSort] = useState(() => column.getSort());

  const columnId = column.getColId();

  const updateCurrentSort = useCallback(() => {
    if (columnId === 'ag-Grid-AutoColumn' && api) {
      const groupedColumnInfo = getGroupedColumnField(api, columnId);
      if (groupedColumnInfo) {
        const columnState = api.getColumnState();
        const groupedColumnState = columnState.find((col) => {
          return col.colId === groupedColumnInfo.colId;
        });
        setCurrentSort(groupedColumnState?.sort || null);
        return;
      }
    }
    setCurrentSort(column.getSort());
  }, [columnId, api, column]);

  const handleSort = (sortDirection) => {
    const groupedColumnInfo = getGroupedColumnField(api, columnId);

    if (groupedColumnInfo && columnId === 'ag-Grid-AutoColumn' && api) {
      if (sortDirection === null) {
        api.applyColumnState({
          defaultState: { sort: null, sortIndex: null },
          state: []
        });
      } else {
        api.applyColumnState({
          defaultState: { sort: null, sortIndex: null },
          state: [{ colId: groupedColumnInfo.colId, sort: sortDirection, sortIndex: 0 }]
        });
      }
      setTimeout(() => updateCurrentSort(), 0);
    } else {
      setSort(sortDirection, false);
    }
  };

  const onSortAsc = () => handleSort('asc');
  const onSortDesc = () => handleSort('desc');
  const onSortNone = () => handleSort(null);

  useEffect(() => {
    if (!api) {
      const width = column.getActualWidth();
      if (width) setColumnWidth(width);
      return;
    }

    const columnId = column.getColId();

    const updateWidth = () => {
      const width = column.getActualWidth();
      if (width) {
        setColumnWidth((prevWidth) => {
          return width !== prevWidth ? width : prevWidth;
        });
      }
    };

    const onColumnResized = (event) => {
      if (!event.columns || event.columns.some((col) => col.getColId() === columnId)) {
        updateWidth();
      }
    };

    const onColumnMoved = () => updateWidth();
    const onColumnVisible = () => updateWidth();
    const onSortChanged = () => updateCurrentSort();

    api.addEventListener('columnResized', onColumnResized);
    api.addEventListener('columnMoved', onColumnMoved);
    api.addEventListener('columnVisible', onColumnVisible);
    api.addEventListener('sortChanged', onSortChanged);

    updateWidth();
    updateCurrentSort();

    return () => {
      api.removeEventListener('columnResized', onColumnResized);
      api.removeEventListener('columnMoved', onColumnMoved);
      api.removeEventListener('columnVisible', onColumnVisible);
      api.removeEventListener('sortChanged', onSortChanged);
    };
  }, [api, column, updateCurrentSort]);

  return (
    <div
      className="group flex w-full cursor-pointer items-center justify-between gap-1"
      onClick={(e) => e.stopPropagation()}>
      <Typography
        variant="p"
        size="small"
        weight="semibold"
        className="block min-w-0 truncate"
        style={
          columnWidth > 0
            ? {
                maxWidth: `${columnWidth - (enableSorting ? HEADER_SORT_RESERVE : HEADER_NO_SORT_RESERVE)}px`
              }
            : undefined
        }>
        {displayName}
      </Typography>
      {enableSorting && (
        <>
          <Button
            icon="order-newest-first-linear"
            variant="link"
            size="sm"
            onClick={onSortAsc}
            color="neutral"
            iconShade={700}
            iconSize={12}
            tooltip="A-Z"
            className="bg-neutral-75 p-1.5"
            wrapperClassName={getButtonClasses(!currentSort)}
          />
          <Button
            icon="order-oldest-first-linear"
            variant="link"
            size="xs"
            onClick={onSortDesc}
            color="neutral"
            iconShade={700}
            iconSize={12}
            tooltip="Z-A"
            className="bg-neutral-75 p-1.5"
            wrapperClassName={getButtonClasses(currentSort === 'asc')}
          />
          <Button
            icon="arrow-swap-down-linear"
            variant="link"
            size="sm"
            onClick={onSortNone}
            color="neutral"
            iconShade={700}
            iconSize={12}
            tooltip="Manual"
            className="bg-neutral-75 p-1.5"
            wrapperClassName={getButtonClasses(currentSort === 'desc')}
          />
        </>
      )}
    </div>
  );
};
